import type { PolicyNamesAndAddressesViewModel } from 'src/api/types/PolicyNamesAndAddressesViewModel';
import type { UpdatePolicyNameRequest, UpdatePolicyAddressRequest, UpdatePolicyNamesAndAddressesRequest } from 'src/api/services/policy.service';

import dayjs from 'dayjs';

import { formatZipcode } from 'src/sections/employer-data/shared/utils/policyUtils';

import type {
  NamesAndAddressesDraft,
  NamesAndAddressesNameDraft,
  NamesAndAddressesAddressDraft,
  NamesAndAddressesNamePatch,
  NamesAndAddressesAddressPatch,
  NamesAndAddressesDraftDelta,
} from './names-and-addresses.types';

let nextRowKey = 0;

const createRowKey = (prefix: string, id?: number | null): string => `${prefix}-${id ?? 'new'}-${nextRowKey++}`;

const toEditDate = (value: string | null | undefined): string => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : value;
};

const toAddressDraft = (source: any, prefix: string): NamesAndAddressesAddressDraft => ({
  rowKey: createRowKey(prefix, source?.policyAddressId),
  policyAddressId: source?.policyAddressId ?? null,
  addressId: source?.address?.addressId ?? null,
  street: source?.address?.street ?? '',
  city: source?.address?.city ?? '',
  // Legacy data uses 0 as a "no state" sentinel; treat it the same as null.
  stateCode: source?.address?.stateCode ? source.address.stateCode : null,
  zipcode: source?.address?.zipcode ?? '',
  email: source?.address?.email ?? '',
  effectiveDate: toEditDate(source?.address?.addressEffectiveDate),
  expirationDate: toEditDate(source?.address?.addressExpirationDate),
});

const toNameDraft = (source: any, prefix: string): NamesAndAddressesNameDraft => ({
  rowKey: createRowKey(prefix, source?.nameId),
  nameId: source?.nameId ?? null,
  name: source?.name ?? source?.employerName ?? '',
  fein: source?.feinLastFive ?? '',
  effectiveDate: toEditDate(source?.policyEffectiveDate),
  expirationDate: toEditDate(source?.policyExpirationDate),
  email: source?.emailAddress ?? '',
  mailingAddress: source?.mailingAddress ? toAddressDraft(source.mailingAddress, `${prefix}-mailing`) : null,
  addresses: (source?.addresses ?? []).map((address: any) => toAddressDraft(address, `${prefix}-address`)),
});

export const initializeNamesAndAddressesDraft = (source: PolicyNamesAndAddressesViewModel): NamesAndAddressesDraft => ({
  primaryEmployer: toNameDraft(source.employerNameAndAddress, 'primary'),
  agency: toNameDraft(source.agencyNameAndAddress, 'agency'),
  additionalNames: (source.additionalNamesAndAddresses ?? []).map((name) => toNameDraft(name, 'additional')),
  deletedAdditionalNameIds: [],
  deletedAdditionalAddressIds: [],
});

const toAddressPayload = (
  address: NamesAndAddressesAddressDraft,
  previousAddress?: NamesAndAddressesAddressDraft | null
): UpdatePolicyAddressRequest => {
  const payload: UpdatePolicyAddressRequest = {
    policyAddressId: address.policyAddressId ?? undefined,
    addressId: address.addressId ?? undefined,
  };
  if (address.street !== previousAddress?.street) payload.street = address.street;
  if (address.city !== previousAddress?.city) payload.city = address.city;
  if (address.stateCode !== previousAddress?.stateCode) payload.stateCode = address.stateCode ?? undefined;
  if (address.zipcode !== previousAddress?.zipcode) payload.zipcode = address.zipcode;
  if (address.email !== previousAddress?.email) payload.email = address.email;
  if (address.effectiveDate !== previousAddress?.effectiveDate) payload.effectiveDate = address.effectiveDate;
  if (address.expirationDate !== previousAddress?.expirationDate) payload.expirationDate = address.expirationDate;
  return payload;
};

const getMailingAddressPayload = (
  name: NamesAndAddressesNameDraft,
  previousName: NamesAndAddressesNameDraft | null | undefined,
  includeDates: boolean
): UpdatePolicyAddressRequest | undefined => {
  if (!name.mailingAddress) {
    return undefined;
  }

  const mailingAddress = {
    ...name.mailingAddress,
    email: name.email,
    effectiveDate: includeDates ? name.mailingAddress.effectiveDate : '',
    expirationDate: includeDates ? name.mailingAddress.expirationDate : '',
  };
  const previousMailingAddress = previousName?.mailingAddress
    ? {
        ...previousName.mailingAddress,
        email: previousName.email,
        effectiveDate: includeDates ? previousName.mailingAddress.effectiveDate : '',
        expirationDate: includeDates ? previousName.mailingAddress.expirationDate : '',
      }
    : null;

  const payload = toAddressPayload(mailingAddress, previousMailingAddress);
  return Object.keys(payload).length > 2 ? payload : undefined;
};

const getChangedAddressPayloads = (
  addresses: NamesAndAddressesAddressDraft[],
  previousAddresses: NamesAndAddressesAddressDraft[]
): UpdatePolicyAddressRequest[] =>
  addresses
    .map((address) => toAddressPayload(address, previousAddresses.find((previous) => previous.rowKey === address.rowKey)))
    .filter((address, index) => !previousAddresses[index] || Object.keys(address).length > 2);

const toNamePayload = (
  name: NamesAndAddressesNameDraft,
  previousName?: NamesAndAddressesNameDraft | null,
  includeDates = true
): UpdatePolicyNameRequest => {
  const payload: UpdatePolicyNameRequest = { nameId: name.nameId ?? undefined };
  if (name.name !== previousName?.name) payload.name = name.name;
  if (name.fein !== previousName?.fein) payload.fein = name.fein;
  if (includeDates && name.effectiveDate !== previousName?.effectiveDate) payload.effectiveDate = name.effectiveDate;
  if (includeDates && name.expirationDate !== previousName?.expirationDate) payload.expirationDate = name.expirationDate;
  const mailingAddress = getMailingAddressPayload(name, previousName, includeDates);
  if (mailingAddress && Object.keys(mailingAddress).length > 2) payload.mailingAddress = mailingAddress;

  const previousAddresses = previousName?.addresses ?? [];
  const changedAddresses = getChangedAddressPayloads(name.addresses, previousAddresses);
  if (changedAddresses.length > 0) payload.addresses = changedAddresses;
  return payload;
};

export const buildNamesAndAddressesPayload = (
  snapshot: NamesAndAddressesDraft,
  draft: NamesAndAddressesDraft
): UpdatePolicyNamesAndAddressesRequest => {
  const previousNamesByKey = new Map(snapshot.additionalNames.map((name) => [name.rowKey, name]));
  const namesAndAddresses: UpdatePolicyNamesAndAddressesRequest = {
  };
  if (draft.deletedAdditionalNameIds.length > 0) namesAndAddresses.deletedAdditionalNameIds = draft.deletedAdditionalNameIds;
  if (draft.deletedAdditionalAddressIds.length > 0) namesAndAddresses.deletedAdditionalAddressIds = draft.deletedAdditionalAddressIds;
  const primaryEmployer = toNamePayload(draft.primaryEmployer, snapshot.primaryEmployer, false);
  const agency = toNamePayload(draft.agency, snapshot.agency, false);
  if (Object.keys(primaryEmployer).length > 1) namesAndAddresses.primaryEmployer = primaryEmployer;
  if (Object.keys(agency).length > 1) namesAndAddresses.agency = agency;
  const additionalNames = draft.additionalNames
    .map((name) => toNamePayload(name, previousNamesByKey.get(name.rowKey)))
    .filter((name) => Object.keys(name).length > 1);
  if (additionalNames.length > 0) namesAndAddresses.additionalNames = additionalNames;
  return namesAndAddresses;
};

export const hasNamesAndAddressesChanges = (snapshot: NamesAndAddressesDraft, draft: NamesAndAddressesDraft): boolean =>
  JSON.stringify(snapshot) !== JSON.stringify(draft);

const addressPatchFields: Array<keyof NamesAndAddressesAddressPatch> = [
  'street',
  'city',
  'stateCode',
  'zipcode',
  'email',
  'effectiveDate',
  'expirationDate',
];

const getAddressPatch = (
  previous: NamesAndAddressesAddressDraft,
  next: NamesAndAddressesAddressDraft
): NamesAndAddressesAddressPatch => {
  const patch: NamesAndAddressesAddressPatch = {};
  addressPatchFields.forEach((fieldName) => {
    if (next[fieldName] !== previous[fieldName]) {
      (patch as Record<string, unknown>)[fieldName] = next[fieldName];
    }
  });
  return patch;
};

const getNamePatch = (
  previous: NamesAndAddressesNameDraft,
  next: NamesAndAddressesNameDraft
): NamesAndAddressesNamePatch => {
  const patch: NamesAndAddressesNamePatch = {};
  (['name', 'fein', 'effectiveDate', 'expirationDate', 'email'] as const).forEach((fieldName) => {
    if (next[fieldName] !== previous[fieldName]) {
      patch[fieldName] = next[fieldName];
    }
  });

  if (previous.mailingAddress && next.mailingAddress) {
    const mailingAddressPatch = getAddressPatch(previous.mailingAddress, next.mailingAddress);
    if (Object.keys(mailingAddressPatch).length > 0) patch.mailingAddress = mailingAddressPatch;
  }

  const previousAddressesById = new Map(
    previous.addresses.filter((address) => address.policyAddressId !== null).map((address) => [address.policyAddressId, address])
  );
  const addressPatches: Record<number, NamesAndAddressesAddressPatch> = {};
  const newAddresses: NamesAndAddressesAddressDraft[] = [];
  next.addresses.forEach((address) => {
    if (address.policyAddressId === null) {
      newAddresses.push(address);
      return;
    }
    const previousAddress = previousAddressesById.get(address.policyAddressId);
    if (!previousAddress) {
      newAddresses.push(address);
      return;
    }
    const addressPatch = getAddressPatch(previousAddress, address);
    if (Object.keys(addressPatch).length > 0) addressPatches[address.policyAddressId] = addressPatch;
  });
  if (Object.keys(addressPatches).length > 0) patch.addressPatches = addressPatches;
  if (newAddresses.length > 0) patch.newAddresses = newAddresses;

  return patch;
};

const applyAddressPatch = (
  address: NamesAndAddressesAddressDraft,
  patch?: NamesAndAddressesAddressPatch
): NamesAndAddressesAddressDraft => (patch ? { ...address, ...patch } : address);

const applyNamePatch = (
  name: NamesAndAddressesNameDraft,
  patch?: NamesAndAddressesNamePatch
): NamesAndAddressesNameDraft => {
  if (!patch) return name;
  const { mailingAddress: mailingAddressPatch, addressPatches, newAddresses, ...scalarPatch } = patch;
  const merged: NamesAndAddressesNameDraft = { ...name, ...scalarPatch };
  if (mailingAddressPatch && merged.mailingAddress) {
    merged.mailingAddress = applyAddressPatch(merged.mailingAddress, mailingAddressPatch);
  }
  let addresses = merged.addresses;
  if (addressPatches) {
    addresses = addresses.map((address) =>
      address.policyAddressId !== null && addressPatches[address.policyAddressId]
        ? applyAddressPatch(address, addressPatches[address.policyAddressId])
        : address
    );
  }
  if (newAddresses && newAddresses.length > 0) {
    addresses = [...addresses, ...newAddresses];
  }
  merged.addresses = addresses;
  return merged;
};

// Changed-values-only representation of edits vs. a fresh view-data snapshot, for cross-tab-switch persistence without duplicating unedited read data.
export const getNamesAndAddressesDraftDelta = (
  snapshot: NamesAndAddressesDraft,
  draft: NamesAndAddressesDraft
): NamesAndAddressesDraftDelta => {
  const delta: NamesAndAddressesDraftDelta = {};

  const primaryEmployerPatch = getNamePatch(snapshot.primaryEmployer, draft.primaryEmployer);
  if (Object.keys(primaryEmployerPatch).length > 0) delta.primaryEmployer = primaryEmployerPatch;

  const agencyPatch = getNamePatch(snapshot.agency, draft.agency);
  if (Object.keys(agencyPatch).length > 0) delta.agency = agencyPatch;

  const previousNamesById = new Map(
    snapshot.additionalNames.filter((name) => name.nameId !== null).map((name) => [name.nameId, name])
  );
  const additionalNamePatches: Record<number, NamesAndAddressesNamePatch> = {};
  const newAdditionalNames: NamesAndAddressesNameDraft[] = [];
  draft.additionalNames.forEach((name) => {
    if (name.nameId === null) {
      newAdditionalNames.push(name);
      return;
    }
    const previousName = previousNamesById.get(name.nameId);
    if (!previousName) {
      newAdditionalNames.push(name);
      return;
    }
    const namePatch = getNamePatch(previousName, name);
    if (Object.keys(namePatch).length > 0) additionalNamePatches[name.nameId] = namePatch;
  });
  if (Object.keys(additionalNamePatches).length > 0) delta.additionalNamePatches = additionalNamePatches;
  if (newAdditionalNames.length > 0) delta.newAdditionalNames = newAdditionalNames;

  if (draft.deletedAdditionalNameIds.length > 0) delta.deletedAdditionalNameIds = draft.deletedAdditionalNameIds;
  if (draft.deletedAdditionalAddressIds.length > 0) delta.deletedAdditionalAddressIds = draft.deletedAdditionalAddressIds;

  return delta;
};

export const applyNamesAndAddressesDraftDelta = (
  snapshot: NamesAndAddressesDraft,
  delta: NamesAndAddressesDraftDelta | null | undefined
): NamesAndAddressesDraft => {
  if (!delta) return snapshot;

  const additionalNames = snapshot.additionalNames.map((name) =>
    name.nameId !== null && delta.additionalNamePatches?.[name.nameId]
      ? applyNamePatch(name, delta.additionalNamePatches[name.nameId])
      : name
  );

  return {
    primaryEmployer: applyNamePatch(snapshot.primaryEmployer, delta.primaryEmployer),
    agency: applyNamePatch(snapshot.agency, delta.agency),
    additionalNames: delta.newAdditionalNames && delta.newAdditionalNames.length > 0
      ? [...additionalNames, ...delta.newAdditionalNames]
      : additionalNames,
    deletedAdditionalNameIds: delta.deletedAdditionalNameIds ?? snapshot.deletedAdditionalNameIds,
    deletedAdditionalAddressIds: delta.deletedAdditionalAddressIds ?? snapshot.deletedAdditionalAddressIds,
  };
};

const validateAddress = (
  errors: Record<string, string>,
  address: NamesAndAddressesAddressDraft,
  prefix: string,
  required: boolean
): void => {
  if (required && !address.street.trim()) errors[`${prefix}.street`] = 'Street is required.';
  if (required && !address.city.trim()) errors[`${prefix}.city`] = 'City is required.';
  if (required && !address.stateCode) errors[`${prefix}.stateCode`] = 'State is required.';
  if (required && !address.zipcode.trim()) errors[`${prefix}.zipcode`] = 'ZIP is required.';
  if (address.effectiveDate && !/^\d{2}\/\d{2}\/\d{4}$/.test(address.effectiveDate)) errors[`${prefix}.effectiveDate`] = 'Date must use MM/DD/YYYY.';
  if (address.expirationDate && !/^\d{2}\/\d{2}\/\d{4}$/.test(address.expirationDate)) errors[`${prefix}.expirationDate`] = 'Date must use MM/DD/YYYY.';
};

const validateName = (
  errors: Record<string, string>,
  name: NamesAndAddressesNameDraft,
  prefix: string,
  required: boolean,
  mailingAddressRequired: boolean,
  previousName?: NamesAndAddressesNameDraft
): void => {
  if (required && !name.name.trim()) errors[`${prefix}.name`] = 'Name is required.';
  if (name.name.length > (prefix === 'agency' ? 30 : 90)) errors[`${prefix}.name`] = 'Name exceeds the maximum length.';
  if (name.email.length > 42) errors[`${prefix}.email`] = 'Email exceeds the maximum length.';
  if (mailingAddressRequired && !name.mailingAddress) errors[`${prefix}.mailingAddress`] = 'Mailing address is required.';
  if (name.mailingAddress) validateAddress(errors, name.mailingAddress, `${prefix}.mailingAddress`, required);

  // When a previous (pre-edit) version of this name is known, skip validating any of its
  // addresses that are unchanged from that snapshot: an existing address missing required
  // fields is acceptable as long as the user hasn't touched it this session.
  const previousAddressesById = previousName
    ? new Map(
        previousName.addresses
          .filter((previousAddress) => previousAddress.policyAddressId !== null)
          .map((previousAddress) => [previousAddress.policyAddressId as number, previousAddress])
      )
    : undefined;

  name.addresses.forEach((address, index) => {
    const previousAddress =
      address.policyAddressId !== null ? previousAddressesById?.get(address.policyAddressId) : undefined;
    const addressIsUnedited =
      previousAddressesById !== undefined &&
      previousAddress !== undefined &&
      Object.keys(getAddressPatch(previousAddress, address)).length === 0;

    if (addressIsUnedited) {
      return;
    }

    validateAddress(errors, address, `${prefix}.addresses.${index}`, true);
  });
};

export const validateNamesAndAddressesDraft = (
  draft: NamesAndAddressesDraft,
  snapshot?: NamesAndAddressesDraft
): Record<string, string> => {
  const errors: Record<string, string> = {};

  // Primary employer / agency are always shown and editable, but their pre-existing data can
  // still be incomplete; only enforce required fields on them if they were actually edited this
  // session, so an unrelated edit elsewhere (e.g. an additional name) doesn't get blocked.
  const primaryEmployerIsUnedited =
    snapshot !== undefined && Object.keys(getNamePatch(snapshot.primaryEmployer, draft.primaryEmployer)).length === 0;
  if (!primaryEmployerIsUnedited) {
    validateName(errors, draft.primaryEmployer, 'primaryEmployer', true, true, snapshot?.primaryEmployer);
  }

  const agencyIsUnedited =
    snapshot !== undefined && Object.keys(getNamePatch(snapshot.agency, draft.agency)).length === 0;
  if (!agencyIsUnedited) {
    validateName(errors, draft.agency, 'agency', true, true, snapshot?.agency);
  }

  const snapshotNamesById = new Map(
    (snapshot?.additionalNames ?? [])
      .filter((previousName) => previousName.nameId !== null)
      .map((previousName) => [previousName.nameId as number, previousName])
  );

  draft.additionalNames.forEach((name, index) => {
    const prefix = `additionalNames.${index}`;
    const isNewName = name.nameId === null;
    const previousName = isNewName ? undefined : snapshotNamesById.get(name.nameId as number);
    // An existing additional name/address that hasn't been edited this session is acceptable
    // as-is, even if it's missing required fields from before this refactor's edit UI existed.
    const nameIsUnedited =
      snapshot !== undefined &&
      previousName !== undefined &&
      Object.keys(getNamePatch(previousName, name)).length === 0;

    if (nameIsUnedited) {
      return;
    }

    validateName(errors, name, prefix, true, false, previousName);
    if (isNewName && name.addresses.length === 0) errors[`${prefix}.addresses`] = 'At least one address is required.';
  });
  return errors;
};

const formatSummaryValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '(blank)';
  if (typeof value === 'object') return JSON.stringify(value);
  // At this point, value is guaranteed to be a primitive (not null, undefined, empty string, or object)
  return String(value as string | number | boolean | symbol | bigint);
};

const formatFieldLabel = (prefix: string, fieldName: string): string => {
  const label = fieldName
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (character) => character.toUpperCase());
  return `${prefix} ${label}`;
};

const appendFieldDelta = (
  summaries: string[],
  label: string,
  previousValue: unknown,
  newValue: unknown
) => {
  const previousText = formatSummaryValue(previousValue);
  const newText = formatSummaryValue(newValue);
  if (previousText !== newText) {
    summaries.push(`${label} changed from ${previousText} to ${newText}`);
  }
};

const appendAddressDeltas = (
  summaries: string[],
  prefix: string,
  previousAddress: NamesAndAddressesAddressDraft | null,
  newAddress: NamesAndAddressesAddressDraft | null,
  includeDates = true
) => {
  const previous = previousAddress ?? ({} as NamesAndAddressesAddressDraft);
  const next = newAddress ?? ({} as NamesAndAddressesAddressDraft);
  const addressFields: Array<keyof NamesAndAddressesAddressDraft> = ['street', 'city', 'stateCode', 'zipcode', 'email'];
  if (includeDates) addressFields.push('effectiveDate', 'expirationDate');
  addressFields.forEach((fieldName) => {
    appendFieldDelta(summaries, formatFieldLabel(prefix, fieldName), previous[fieldName], next[fieldName]);
  });
};

type StateLookup = { stateCode: number; stateAbbreviation: string };

const formatAddressSummary = (
  address: NamesAndAddressesAddressDraft,
  stateLookups: StateLookup[]
): string => {
  const state = stateLookups.find((lookup) => lookup.stateCode === address.stateCode)?.stateAbbreviation
    ?? (address.stateCode ? String(address.stateCode) : '');
  const locality = [address.city, state].filter(Boolean).join(' ');
  return [address.street, locality, formatZipcode(address.zipcode)].filter(Boolean).join(', ');
};

const formatAddressesSummary = (
  addresses: NamesAndAddressesAddressDraft[],
  stateLookups: StateLookup[]
): string => addresses.map((address) => formatAddressSummary(address, stateLookups)).filter(Boolean).join('; ');

const appendAdditionalAddressChanges = (
  summaries: string[],
  parentName: string,
  previousAddresses: NamesAndAddressesAddressDraft[],
  newAddresses: NamesAndAddressesAddressDraft[],
  stateLookups: StateLookup[]
) => {
  const previousByKey = new Map(previousAddresses.map((address) => [address.rowKey, address]));
  const newByKey = new Map(newAddresses.map((address) => [address.rowKey, address]));

  newAddresses.forEach((address, index) => {
    const previousAddress = previousByKey.get(address.rowKey);
    if (!previousAddress) {
      summaries.push(
        `Additional employer address added ${formatAddressSummary(address, stateLookups)} under ${parentName}`
      );
      return;
    }

    // Address kept from the snapshot but with edited fields (street/city/state/zip/dates); the
    // added/removed checks above only cover row identity, not per-field edits within a row.
    appendAddressDeltas(summaries, `${parentName} address ${index + 1}`, previousAddress, address);
  });

  previousAddresses.forEach((address) => {
    if (!newByKey.has(address.rowKey)) {
      summaries.push(
        `Additional employer address removed ${formatAddressSummary(address, stateLookups)} under ${parentName}`
      );
    }
  });
};

const appendNameDeltas = (
  summaries: string[],
  prefix: string,
  previousName: NamesAndAddressesNameDraft | null,
  newName: NamesAndAddressesNameDraft | null,
  includeAdditionalAddresses = true,
  includeDates = true
) => {
  const previous = previousName ?? ({} as NamesAndAddressesNameDraft);
  const next = newName ?? ({} as NamesAndAddressesNameDraft);
  const nameFields: Array<keyof NamesAndAddressesNameDraft> = ['name', 'fein', 'email'];
  if (includeDates) nameFields.splice(2, 0, 'effectiveDate', 'expirationDate');
  nameFields.forEach((fieldName) => {
    appendFieldDelta(summaries, formatFieldLabel(prefix, fieldName), previous[fieldName], next[fieldName]);
  });
  appendAddressDeltas(
    summaries,
    `${prefix} mailing address`,
    previous.mailingAddress,
    next.mailingAddress,
    includeDates
  );

  if (includeAdditionalAddresses) {
    const addressCount = Math.max(previous.addresses?.length ?? 0, next.addresses?.length ?? 0);
    for (let index = 0; index < addressCount; index += 1) {
      appendAddressDeltas(
        summaries,
        `${prefix} address ${index + 1}`,
        previous.addresses?.[index] ?? null,
        next.addresses?.[index] ?? null
      );
    }
  }
};

const appendAdditionalNameSummary = (
  summaries: string[],
  previousName: NamesAndAddressesNameDraft | null,
  newName: NamesAndAddressesNameDraft | null,
  stateLookups: StateLookup[]
) => {
  if (!previousName) {
    appendAddedNameSummary(summaries, newName, stateLookups);
    return;
  }

  if (!newName) {
    appendRemovedNameSummary(summaries, previousName, stateLookups);
    return;
  }

  appendUpdatedNameSummary(summaries, previousName, newName, stateLookups);
};

const appendAddedNameSummary = (
  summaries: string[],
  name: NamesAndAddressesNameDraft | null,
  stateLookups: StateLookup[]
): void => {
  if (!name) return;
  const addresses = formatAddressesSummary(name.addresses, stateLookups);
  const summary = addresses ? `${name.name} with address of ${addresses}` : `${name.name} added`;
  summaries.push(summary);
};

const appendRemovedNameSummary = (
  summaries: string[],
  name: NamesAndAddressesNameDraft,
  stateLookups: StateLookup[]
): void => {
  const addresses = formatAddressesSummary(name.addresses, stateLookups);
  const summary = addresses
    ? `${name.name} with address of ${addresses} removed`
    : `${name.name} removed`;
  summaries.push(summary);
};

const appendUpdatedNameSummary = (
  summaries: string[],
  previousName: NamesAndAddressesNameDraft,
  newName: NamesAndAddressesNameDraft,
  stateLookups: StateLookup[]
): void => {
  const displayName = newName.name || previousName.name;
  appendNameDeltas(summaries, `Additional employer ${displayName}`, previousName, newName, false);
  appendAdditionalAddressChanges(
    summaries,
    displayName,
    previousName.addresses,
    newName.addresses,
    stateLookups
  );
};

export const getNamesAndAddressesChangedFieldSummaries = (
  snapshot: NamesAndAddressesDraft,
  draft: NamesAndAddressesDraft,
  stateLookups: StateLookup[] = []
): string[] => {
  const summaries: string[] = [];
  appendNameDeltas(summaries, 'Primary employer', snapshot.primaryEmployer, draft.primaryEmployer, true, false);
  appendNameDeltas(summaries, 'Agency', snapshot.agency, draft.agency, true, false);

  const previousNamesByKey = new Map(snapshot.additionalNames.map((name) => [name.rowKey, name]));
  const draftNamesByKey = new Map(draft.additionalNames.map((name) => [name.rowKey, name]));
  const nameKeys = new Set([...previousNamesByKey.keys(), ...draftNamesByKey.keys()]);
  nameKeys.forEach((rowKey) => {
    appendAdditionalNameSummary(
      summaries,
      previousNamesByKey.get(rowKey) ?? null,
      draftNamesByKey.get(rowKey) ?? null,
      stateLookups
    );
  });

  return summaries;
};
